// src/store/slices/wishlistSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import wishlistService from '../../services/wishlistService';

const getInitialWishlist = () => {
  try {
    return wishlistService.loadLocal();
  } catch {
    return [];
  }
};

const initialState = {
  items: getInitialWishlist(),
  loading: false,
  error: null,
  mode: 'guest',
};

const normalizeProduct = (product) => ({
  id: product?.product_id || product?.productId || product?.id,
  title: product?.title || product?.name,
  price: product?.price,
  image: product?.image,
});

export const fetchWishlist = createAsyncThunk('wishlist/fetchWishlist', async (userId, { rejectWithValue }) => {
  try {
    if (!userId) {
      return { items: wishlistService.loadLocal(), mode: 'guest' };
    }
    const remote = await wishlistService.fetchRemote(userId);
    return { items: remote, mode: 'api' };
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch wishlist');
  }
});

export const addToWishlist = createAsyncThunk(
  'wishlist/addToWishlist',
  async ({ product, userId }, { rejectWithValue }) => {
    try {
      if (!product) {
        throw new Error('Product data is required');
      }
      if (!userId) {
        const items = wishlistService.addLocal(product);
        return { items, mode: 'guest' };
      }
      const normalized = normalizeProduct(product);
      if (!normalized.id) {
        throw new Error('Product ID is required');
      }
      const items = await wishlistService.addRemote(userId, normalized.id);
      return { items, mode: 'api' };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to add to wishlist');
    }
  }
);

export const removeFromWishlist = createAsyncThunk(
  'wishlist/removeFromWishlist',
  async ({ productId, userId }, { rejectWithValue }) => {
    try {
      if (!productId) {
        throw new Error('Product ID is required');
      }
      if (!userId) {
        const items = wishlistService.removeLocal(productId);
        return { items, mode: 'guest' };
      }
      const items = await wishlistService.removeRemote(userId, productId);
      return { items, mode: 'api' };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to remove from wishlist');
    }
  }
);

export const syncWishlistAfterLogin = createAsyncThunk(
  'wishlist/syncAfterLogin',
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) {
        return { items: wishlistService.loadLocal(), mode: 'guest' };
      }
      const items = await wishlistService.syncLocalToRemote(userId);
      return { items, mode: 'api' };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to sync wishlist');
    }
  }
);

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {
    clearLocalWishlist: (state) => {
      wishlistService.clearLocal();
      state.items = [];
      state.mode = 'guest';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || [];
        state.mode = action.payload.mode || 'guest';
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addToWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || [];
        state.mode = action.payload.mode || state.mode;
      })
      .addCase(addToWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(removeFromWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || [];
        state.mode = action.payload.mode || state.mode;
      })
      .addCase(removeFromWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(syncWishlistAfterLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(syncWishlistAfterLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || [];
        state.mode = action.payload.mode || 'api';
      })
      .addCase(syncWishlistAfterLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearLocalWishlist } = wishlistSlice.actions;
export default wishlistSlice.reducer;